/**
 * StepIndicator — progress dots and current task name for the CoPilot Guide header.
 */

import type { CoPilotGuideEngine } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';

export function StepIndicator({ engine }: { engine: CoPilotGuideEngine }) {
  const { t } = useTranslation();
  const tasks = engine.getTasks();
  const state = engine.getState();
  // Filter to user-visible tasks (exclude Escape and Terminal)
  const visibleTasks = tasks.filter((t) => t.type !== 'Escape' && t.type !== 'Terminal');
  const currentIdx = visibleTasks.findIndex((t) => t.id === state.currentTaskId);
  const completedCount = visibleTasks.filter((t) => state.completedTasks.has(t.id)).length;
  const total = visibleTasks.length;
  const stepNum = Math.max(currentIdx + 1, completedCount + 1);
  const currentTaskName = currentIdx >= 0 ? visibleTasks[currentIdx].name : '';

  return (
    <div
      className="flex items-center gap-2 min-w-0"
      aria-label={t('copilot.guide.aria.stepOf', { step: stepNum, total })}
      data-testid="guide-step-indicator"
    >
      <div className="flex gap-1">
        {visibleTasks.map((task, idx) => (
          <div
            key={task.id}
            data-testid={`guide-step-dot-${idx}`}
            className={`h-1.5 w-4 rounded-full transition-colors ${
              state.completedTasks.has(task.id)
                ? 'bg-primary'
                : idx === currentIdx
                  ? 'bg-primary/50'
                  : 'bg-muted'
            }`}
          />
        ))}
      </div>
      <span
        className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline"
        data-testid="guide-current-task"
      >
        {currentTaskName && `${currentTaskName}`}
      </span>
    </div>
  );
}
